import { useEffect, useState } from "react";
import { supabase } from "../../../../lib/supabase";
import { useAuth } from "../../../../hooks/useAuth";
import { useToast } from "../../../../hooks/useToast";
import DashboardLayout from "../../../../components/feature/DashboardLayout";

interface CaregiverProfile {
  id: string;
  full_name: string | null;
  phone: string | null;
  address: string | null;
  created_at: string;
  appointment_count?: number;
}

const navItems = [
  { label: "Dashboard", path: "/dashboard/admin", icon: "ri-dashboard-line" },
  { label: "Clients", path: "/dashboard/admin/clients", icon: "ri-user-line" },
  { label: "Caregivers", path: "/dashboard/admin/caregivers", icon: "ri-user-heart-line" },
  { label: "Appointments", path: "/dashboard/admin/appointments", icon: "ri-calendar-event-line" },
  { label: "Care Plans", path: "/dashboard/admin/care-plans", icon: "ri-file-list-3-line" },
  { label: "Settings", path: "/dashboard/admin/settings", icon: "ri-settings-3-line" },
];

export default function AdminCaregivers() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [caregivers, setCaregivers] = useState<CaregiverProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<CaregiverProfile | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formMsg, setFormMsg] = useState("");

  useEffect(() => {
    if (!user) return;
    fetchCaregivers();

    // Real-time subscription for profiles
    const channel = supabase
      .channel('caregivers-admin')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'profiles',
      }, (payload) => {
        fetchCaregivers();
        if (payload.eventType === 'INSERT') showToast('New caregiver added', 'success');
        if (payload.eventType === 'UPDATE') showToast('Caregiver profile updated', 'info');
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const fetchCaregivers = async () => {
    const { data: profiles } = await supabase
      .from("profiles")
      .select("*")
      .eq("role", "caregiver")
      .order("created_at", { ascending: false });

    const list = (profiles || []).map((p: any) => ({
      id: p.id,
      full_name: p.full_name,
      phone: p.phone,
      address: p.address,
      created_at: p.created_at,
      appointment_count: 0,
    }));

    for (const cg of list) {
      const { count } = await supabase
        .from("appointments")
        .select("*", { count: "exact", head: true })
        .eq("caregiver_id", cg.id);
      cg.appointment_count = count || 0;
    }

    setCaregivers(list);
    setLoading(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const filtered = caregivers.filter((c) => {
    const term = search.toLowerCase();
    return (
      (c.full_name?.toLowerCase() || "").includes(term) ||
      (c.phone || "").includes(term)
    );
  });

  const handleAddCaregiver = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormMsg("");
    const fd = new FormData(e.currentTarget);
    const full_name = fd.get("full_name") as string;
    const phone = fd.get("phone") as string;
    const address = fd.get("address") as string;

    // Since we can't create auth users from the frontend easily without edge functions,
    // we'll insert into profiles directly. In production, this would need an edge function.
    const { data: existing, error: checkErr } = await supabase
      .from("profiles")
      .select("id")
      .eq("role", "caregiver")
      .eq("full_name", full_name)
      .maybeSingle();

    if (existing) {
      setFormMsg("A caregiver with this name already exists.");
      setSubmitting(false);
      return;
    }

    // Generate a pseudo-uuid for demo purposes (in production, use an edge function to create auth user)
    const tempId = crypto.randomUUID();
    const { error } = await supabase.from("profiles").insert({
      id: tempId,
      role: "caregiver",
      full_name,
      phone,
      address,
    });

    if (error) {
      setFormMsg("Failed to add caregiver.");
    } else {
      setFormMsg("Caregiver added successfully!");
      setTimeout(() => {
        setShowAddModal(false);
        setFormMsg("");
        fetchCaregivers();
      }, 1200);
    }
    setSubmitting(false);
  };

  return (
    <DashboardLayout navItems={navItems} title="Admin Portal" subtitle="All Caregivers">
      {loading ? (
        <div className="flex items-center gap-3 py-12">
          <div className="w-5 h-5 border-2 border-brand-green-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading caregivers...</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Search + Add */}
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="flex-1 relative w-full">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search caregivers..."
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
              />
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-green-700 transition-all whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line" />
              </div>
              Add Caregiver
            </button>
          </div>

          {/* Caregivers Table / Cards */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {/* Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Caregiver</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Address</th>
                    <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Visits</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((cg) => (
                    <tr key={cg.id} className="hover:bg-soft-gray transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-user-heart-line text-blue-600" />
                            </div>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{cg.full_name || "Unnamed"}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600">{cg.phone || "—"}</td>
                      <td className="px-5 py-3 text-sm text-gray-600 max-w-[200px] truncate">{cg.address || "—"}</td>
                      <td className="px-5 py-3 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">{cg.appointment_count}</span>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-500">{formatDate(cg.created_at)}</td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() => setSelected(cg)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-eye-line text-gray-500" />
                          </div>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <div className="md:hidden divide-y divide-gray-100">
              {filtered.map((cg) => (
                <div key={cg.id} className="p-4" onClick={() => setSelected(cg)}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-user-heart-line text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{cg.full_name || "Unnamed"}</p>
                      <p className="text-xs text-gray-500">{cg.phone || "No phone"}</p>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {cg.appointment_count} assigned visits
                  </div>
                </div>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-12">
                <div className="w-14 h-14 rounded-full bg-soft-gray flex items-center justify-center mx-auto mb-3">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-user-heart-line text-xl text-gray-400" />
                  </div>
                </div>
                <p className="text-sm text-gray-500">No caregivers found.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Caregiver Details</h3>
              <button onClick={() => setSelected(null)} className="p-1 rounded-lg hover:bg-gray-100">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line text-gray-500" />
                </div>
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-user-heart-line text-blue-600 text-lg" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{selected.full_name || "Unnamed"}</p>
                  <p className="text-xs text-gray-500">ID: {selected.id.slice(0, 8)}...</p>
                </div>
              </div>
              <div className="space-y-3 pt-3 border-t border-gray-100">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Phone</span>
                  <span className="text-sm text-gray-900">{selected.phone || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Address</span>
                  <span className="text-sm text-gray-900 text-right max-w-[180px]">{selected.address || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Joined</span>
                  <span className="text-sm text-gray-900">{formatDate(selected.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Assigned Visits</span>
                  <span className="text-sm text-gray-900">{selected.appointment_count}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Caregiver Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddModal(false)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Caregiver</h3>
            {formMsg && (
              <div className={`rounded-lg p-3 border mb-4 ${formMsg.includes("success") ? "bg-brand-green-50 border-brand-green-100 text-brand-green-800" : "bg-red-50 border-red-100 text-red-700"}`}>
                <p className="text-sm text-center">{formMsg}</p>
              </div>
            )}
            <form onSubmit={handleAddCaregiver} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Full Name</label>
                <input
                  name="full_name"
                  type="text"
                  required
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Phone</label>
                <input
                  name="phone"
                  type="tel"
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                  placeholder="773-xxx-xxxx"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Address</label>
                <textarea
                  name="address"
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none resize-none"
                  placeholder="Address..."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-soft-gray rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-brand-green-600 rounded-lg hover:bg-brand-green-700 disabled:bg-brand-green-400 transition-all whitespace-nowrap"
                >
                  {submitting ? "Adding..." : "Add Caregiver"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}